import React, { useState, useEffect } from 'react';
import './TheoreticalSection.css';
import TestWidget from './TestWidget';
import { AuthService } from '../services/auth.service';
import { UserService } from '../services/user.service';

interface TheoreticalSectionProps {
  isLoggedIn: boolean;
}

const TheoreticalSection: React.FC<TheoreticalSectionProps> = ({ isLoggedIn }) => {
  const [showTestWidget, setShowTestWidget] = useState(false);
  const [hasTestResults, setHasTestResults] = useState(false);
  
  // Основной эффект для инициализации данных
  useEffect(() => {
    if (isLoggedIn) {
      const user = AuthService.getUser();
      setHasTestResults(user?.hasTestResults || false);
    } else {
      setHasTestResults(false);
    }
  }, [isLoggedIn]);

  // Эффект для периодической проверки статуса
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const checkUserStatus = async () => {
      if (isLoggedIn && !hasTestResults) {
        try {
          const userData = await UserService.getProfile();
          if (userData.hasTestResults) {
            setHasTestResults(true);
            // Если тест пройден, останавливаем интервал
            clearInterval(intervalId);
          }
        } catch (error) {
          console.error('Error checking user status:', error);
        }
      }
    };

    if (isLoggedIn && !hasTestResults) {
      // Проверяем каждые 2 минуты
      intervalId = setInterval(checkUserStatus, 120000);
      // Также делаем первую проверку сразу
      checkUserStatus();
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isLoggedIn, hasTestResults]);
  
  // Обработчик клика по кнопке
  const handleTestButtonClick = () => {
    if (!isLoggedIn) {
      // Если пользователь не авторизован, скроллим к началу страницы
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else if (!hasTestResults) {
      // Показываем виджет тестирования только если тест не пройден
      setShowTestWidget(true);
    }
  };

  const handleCloseWidget = () => {
    setShowTestWidget(false);
  };

  const handleTestComplete = () => {
    setHasTestResults(true);
  };
  
  return (
    <>
      <section className="theoretical-section" id="about">
        <div className="theoretical-content">
          <h2>ПЕРВЫЙ ЭТАП: ТЕОРЕТИЧЕСКИЙ ТУР</h2>
          
          <div className="steps-list">
            <div className="step-item">
              <span className="step-number">1</span>
              <div className="step-info">
                <h3>Задание</h3>
                <p>Решить задачи по химии и нанотехнологиям на платформе в онлайн формате</p>
              </div>
            </div>

            <div className="step-item">
              <span className="step-number">2</span>
              <div className="step-info">
                <h3>Сроки</h3>
                <p>с 25 мая по 4 октября 2026 года</p>
              </div>
            </div>

            <div className="step-item">
              <span className="step-number">3</span>
              <div className="step-info">
                <h3>Результат</h3>
                <p>Электронные дипломы и сертификаты для победителей и участников</p>
              </div>
            </div>
          </div>

          <button 
            className={`theoretical-button ${isLoggedIn && hasTestResults ? 'disabled' : ''}`}
            onClick={handleTestButtonClick}
            disabled={isLoggedIn && hasTestResults}
          >
            {isLoggedIn 
              ? hasTestResults 
                ? 'РЕЗУЛЬТАТЫ ТЕСТИРОВАНИЯ ОТПРАВЛЕНЫ'
                : 'ПРОЙТИ ТЕОРЕТИЧЕСКИЙ ТУР'
              : 'ПРОЙТИ ТЕОРЕТИЧЕСКИЙ ТУР'
            }
          </button>
          
          {!isLoggedIn && (
            <div className="theoretical-info-message">
              Для участия необходимо авторизоваться
            </div>
          )}
          
          {isLoggedIn && hasTestResults && (
            <div className="theoretical-info-message success">
              Результаты тестирования отправлены на проверку
            </div>
          )}
        </div>
        <div className="theoretical-image"></div>
      </section>

      <TestWidget 
        isVisible={showTestWidget} 
        onClose={handleCloseWidget}
        onTestComplete={handleTestComplete}
      />
    </>
  );
};

export default TheoreticalSection; 